import React, { useState, useContext, useMemo } from 'react';
import { Link, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { isFeatureEnabled, isPhoneCallsEnabled, FEATURES } from '../../../helpers/feature_checker';

import Context from '../../../utils/context';
import SimpleSlidingMenu from '../../ui_kit/utilities/simple_sliding_menu';
import PatientInsights from '../patient_insights';
import MentorInsights from '../mentor_insights';
import PatientDemographics from '../patient_demographics';
import MentorConnect from '../patients/mentor_connect';
import CallsDashboard from '../calls_dashboard';

import styles from './index.module.css';

const createMenuConfig = (key, iconSrc, text, url, pendoTag) => ({
  key,
  icon: <img alt={text.toLowerCase()} src={iconSrc} />,
  text,
  url,
  linkText: text,
  idToScrollTo: key,
  href: url,
  pendoTag,
});

// All possible menu configurations
const MENU_CONFIGS = {
  patientInsights: createMenuConfig(
    'insights',
    'https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/providers/insights.svg',
    'Patient Insights',
    '/provider/insights',
    'profile page navigation - insights'
  ),
  mentorConnect: createMenuConfig(
    'mentor-connect',
    'https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/providers/mentorconnect.svg',
    'Mentees',
    '/provider/mentor-connect',
    'profile page navigation - mentor connect'
  ),
  patientDemographics: createMenuConfig(
    'patient-demographics',
    'https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/providers/referrals.svg',
    'Mentee Demographics',
    '/provider/patient-demographics',
    'profile page navigation - mentee demographics'
  ),
  mentorInsights: createMenuConfig(
    'mentor-insights',
    'https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/providers/mentor-insights-2.svg',
    'Mentor Insights',
    '/provider/mentor-insights',
    'profile page navigation - mentor insights'
  ),
  calls: {
    key: 'calls',
    icon: (
      <svg
        width='16'
        height='16'
        viewBox='0 0 14 14'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        style={{ marginRight: '16px', flexShrink: 0 }}
      >
        <path
          d='M9.33393 9.9457L10.5395 8.74016C10.7018 8.5798 10.9073 8.47003 11.1308 8.42418C11.3544 8.37832 11.5864 8.39835 11.7988 8.48183L13.2681 9.06845C13.4827 9.15557 13.6667 9.30428 13.797 9.49583C13.9272 9.68738 13.9979 9.9132 14 10.1448V12.8358C13.9988 12.9933 13.9656 13.149 13.9026 13.2934C13.8396 13.4379 13.748 13.5681 13.6334 13.6762C13.5188 13.7843 13.3834 13.8681 13.2355 13.9225C13.0877 13.9769 12.9303 14.0008 12.7729 13.9929C2.47744 13.3524 0.400043 4.6338 0.00716727 1.29705C-0.0110703 1.13319 0.00559289 0.967329 0.0560605 0.810374C0.106528 0.653418 0.189656 0.508927 0.299977 0.386405C0.410297 0.263884 0.545309 0.166109 0.69613 0.0995131C0.846951 0.0329174 1.01016 -0.00098981 1.17503 2.19951e-05H3.77447C4.00643 0.000708605 4.23288 0.0707759 4.4247 0.201212C4.61652 0.331647 4.76493 0.516487 4.85084 0.731955L5.43746 2.2012C5.52371 2.41275 5.54571 2.64501 5.50073 2.86899C5.45574 3.09297 5.34576 3.29872 5.18452 3.46056L3.97898 4.66609C3.97898 4.66609 4.67324 9.36446 9.33393 9.9457Z'
          fill='#419DA5'
        />
      </svg>
    ),
    text: 'Calls',
    url: '/provider/calls',
    linkText: 'Calls',
    idToScrollTo: 'calls',
    href: '/provider/calls',
    pendoTag: 'profile page navigation - calls',
  },
};

// Routes component to avoid duplication
const ProviderRoutes = ({ path }) => (
  <Switch>
    <Route exact path='/provider/insights'>
      <PatientInsights />
    </Route>
    <Route exact path='/provider/mentor-connect'>
      <MentorConnect />
    </Route>
    <Route exact path='/provider/patient-demographics'>
      <PatientDemographics />
    </Route>
    <Route exact path='/provider/mentor-insights'>
      <MentorInsights />
    </Route>
    <Route exact path='/provider/calls'>
      <CallsDashboard />
    </Route>
    <Route exact path={path}></Route>
  </Switch>
);

export default function ProviderHome() {
  let { path } = useRouteMatch();
  let isMobile = useContext(Context).viewport.getIsMobile();
  let currentUser = useContext(Context).auth.getCurrentUser();
  let isSuperUser = useContext(Context).auth.isSuperUser();
  let isImpersonating = useContext(Context).auth.isImpersonating();

  // Build menu configuration based on user type
  const menuConfigs = useMemo(() => {
    const isPharma = currentUser.account.account_type === 'pharmaceutical';
    // Show Mentor Connect if: not pharma OR super user is impersonating
    const showMentorConnect = !isPharma || (isSuperUser && isImpersonating);
    // Mentee Demographics is gated by the Patient Demographics feature flag,
    // no longer by the account being pharmaceutical.
    const showDemographics = isFeatureEnabled(currentUser.account, FEATURES.PATIENT_DEMOGRAPHICS);
    const showCalls = isPhoneCallsEnabled(currentUser.account);

    if (isPharma) {
      const configs = [MENU_CONFIGS.patientInsights];
      if (showDemographics) {
        configs.push(MENU_CONFIGS.patientDemographics);
      }
      if (showCalls) {
        configs.push(MENU_CONFIGS.calls);
      }
      configs.push(MENU_CONFIGS.mentorInsights);
      if (showMentorConnect) {
        configs.push(
          isSuperUser
            ? {
                ...MENU_CONFIGS.mentorConnect,
                text: 'Mentees (Super User Only)',
                linkText: 'Mentees (Super User Only)',
              }
            : MENU_CONFIGS.mentorConnect
        );
      }
      return configs;
    }

    // Regular users always see Mentor Connect
    const configs = [MENU_CONFIGS.patientInsights];
    if (showDemographics) {
      configs.push(MENU_CONFIGS.patientDemographics);
    }
    if (showCalls) {
      configs.push(MENU_CONFIGS.calls);
    }
    configs.push(MENU_CONFIGS.mentorConnect, MENU_CONFIGS.mentorInsights);
    return configs;
  }, [currentUser.account, isSuperUser, isImpersonating]);

  // Extract menuLinks and menuItems from configs
  const menuLinks = menuConfigs.map(({ icon, text, url }) => ({ icon, text, url }));
  const menuItems = menuConfigs.map(({ linkText, idToScrollTo, href, pendoTag }) => ({
    linkText,
    idToScrollTo,
    href,
    pendoTag,
  }));

  const location = useLocation();
  let defaultSelectedMenuItem = menuItems[0];
  menuItems.forEach((item) => {
    if (item.href === location.pathname) {
      defaultSelectedMenuItem = item;
    }
  });
  const [selectedMenuItem, setSelectedMenuItem] = useState(defaultSelectedMenuItem);

  if (isMobile) {
    return (
      <div className={`container-fluid ${styles.outer_wrapper}`}>
        <div className='container marg-top-30'>
          <div className='row'>
            <div className='col'>
              <SimpleSlidingMenu
                menuItems={menuItems}
                setMarker={selectedMenuItem}
                setMarkerItem={setSelectedMenuItem}
              />
            </div>
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <ProviderRoutes path={path} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`container-fluid ${styles.outer_wrapper}`}>
      <div className='row'>
        <div className='col-2'>
          <nav className={styles.left_menu}>
            {menuLinks.map((link, index) => (
              <Link
                key={index}
                className={`${styles.menu_link} ${window.location.pathname === link.url && styles.selected}`}
                to={link.url}
              >
                {link.icon}
                <p>{link.text}</p>
              </Link>
            ))}
          </nav>
        </div>
        <div className='col-9'>
          <ProviderRoutes path={path} />
        </div>
        <div className='col-1'></div>
      </div>
    </div>
  );
}
